/**
 * Tabs — Underline-Pattern (Sprint-16-Brand).
 *
 * Active = text-ink + 2px sealwax-Underline (Signature-Marker)
 * Inactive = text-ink-soft, border-transparent, hover-text-ink
 *
 * Für Pill-Style-Tab-Bars (z.B. Apartment-Sub-Tabs Übersicht/Stammdaten/…) →
 * SegmentedControl statt Tabs verwenden.
 */
import {
  createContext,
  useContext,
  useId,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/shared/lib/cn';

interface TabsContext {
  value: string;
  onChange: (next: string) => void;
  id: string;
}

const TabsCtx = createContext<TabsContext | null>(null);

export interface TabsProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (next: string) => void;
  children: ReactNode;
}

export function Tabs({ defaultValue, value, onValueChange, children }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const id = useId();
  const current = value ?? internal;
  const onChange = (next: string) => {
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };
  return <TabsCtx.Provider value={{ value: current, onChange, id }}>{children}</TabsCtx.Provider>;
}

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error('TabsList must be inside <Tabs>');
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const tabs = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'),
    );
    const current = tabs.indexOf(event.target as HTMLButtonElement);
    if (current < 0 || tabs.length === 0) return;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[next]!;
    event.preventDefault();
    tab.focus();
    ctx.onChange(tab.dataset.value!);
  };
  return (
    <div
      role="tablist"
      onKeyDown={onKeyDown}
      className={cn('border-line/50 flex items-center gap-1 border-b', className)}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error('TabsTrigger must be inside <Tabs>');
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      id={`${ctx.id}-tab-${value}`}
      aria-controls={`${ctx.id}-panel-${value}`}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      data-value={value}
      onClick={() => ctx.onChange(value)}
      className={cn(
        '-mb-px px-4 py-3 font-sans text-[13px] font-medium transition-colors',
        'border-b-2',
        active ? 'text-ink border-sealwax' : 'text-ink-soft hover:text-ink border-transparent',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error('TabsContent must be inside <Tabs>');
  if (ctx.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.id}-panel-${value}`}
      aria-labelledby={`${ctx.id}-tab-${value}`}
      className={cn('animate-fade-up pt-6', className)}
    >
      {children}
    </div>
  );
}
